import { Mail, Shield, MessageCircle } from "lucide-react";
import { GAME, FOREST_MIST, DISCORD_URL, ASSETS } from "../data/content";
import GooglePlayBadge from "../components/GooglePlayBadge";
import useReveal from "../hooks/useReveal";
import { trackOutboundClick } from "../lib/analytics";

const onDiscordClick = (placement) =>
  trackOutboundClick(`discord-${placement}`, DISCORD_URL, { placement });

export default function FooterCTA() {
  const ref = useReveal();
  const discordRef = useReveal();
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative isolate overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-10">
        <img
          src={FOREST_MIST}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--background))] via-black/75 to-black/95" />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-28 lg:pt-32 pb-14">
        {/* CTA band — text + cinematic image side-by-side */}
        <div ref={ref} className="reveal grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-body">
              The call of the wild awaits
            </p>
            <h2
              data-testid="footer-cta-title"
              className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-white text-balance leading-[1.05]"
            >
              Take your shot at becoming the top of the leaderboards.
            </h2>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <GooglePlayBadge testId="footer-google-play-badge" size="lg" />
              <div className="text-sm text-white/60 font-body" data-testid="footer-cta-meta">
                <div>{GAME.rating}★ · {GAME.downloads} downloads</div>
                <div className="text-[11px] uppercase tracking-[0.22em] mt-1 text-white/40">
                  Available on Android
                </div>
              </div>
            </div>
          </div>

          {/* Cinematic camp image */}
          <div className="lg:col-span-5">
            <div
              data-testid="footer-cta-image"
              className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-black/60 aspect-[4/3] lg:aspect-[5/4]"
            >
              <img
                src={ASSETS.ctaBanner}
                alt="Hunters resting by a lakeside camp at golden hour"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1400ms] hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-black/10 pointer-events-none" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/8 pointer-events-none rounded-2xl" />
            </div>
          </div>
        </div>

        {/* Discord invite — prominent */}
        <div
          ref={discordRef}
          className="reveal mt-16 lg:mt-20"
          style={{ transitionDelay: "120ms" }}
        >
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-discord-cta"
            onClick={() => onDiscordClick("footer-cta")}
            className="group relative block overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#5865F2]/15 via-[#5865F2]/8 to-transparent p-7 lg:p-9 transition-all duration-500 hover:border-[#5865F2]/50 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-[#5865F2]/15"
          >
            <div className="pointer-events-none absolute -top-24 -right-12 h-56 w-56 rounded-full bg-[#5865F2]/20 blur-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#5865F2] ring-1 ring-white/15 shadow-lg shadow-[#5865F2]/30">
                <DiscordGlyph className="h-7 w-7 text-white" />
              </span>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A5ADFF] font-body">
                  Join the community
                </div>
                <h3 className="font-display text-3xl lg:text-4xl text-white mt-1.5">
                  Hunt with us on Discord.
                </h3>
                <p className="mt-2 text-sm text-white/65 font-body max-w-xl">
                  Swap shot stories, get patch notes first, and team up for
                  Weekend Challenges with hunters from around the globe.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 self-start md:self-center rounded-full bg-white/5 px-5 py-3 text-sm font-semibold text-white border border-white/15 group-hover:bg-[#5865F2] group-hover:border-[#5865F2] transition-all duration-300 font-body">
                <MessageCircle className="h-4 w-4" />
                Join Discord
                <span aria-hidden className="text-base leading-none">→</span>
              </span>
            </div>
          </a>
        </div>

        {/* Divider */}
        <div className="mt-16 rule-glow" />

        {/* Bottom row */}
        <div className="mt-10 grid gap-8 md:grid-cols-3 items-start">
          <div className="flex items-center gap-3">
            <img
              src={ASSETS.appIcon}
              alt=""
              aria-hidden="true"
              className="h-12 w-12 rounded-md object-cover ring-1 ring-white/15"
              draggable="false"
            />
            <div>
              <div className="font-display text-xl text-white">{GAME.name}</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-body">
                © {year} {GAME.developer}
              </div>
            </div>
          </div>

          <div className="md:justify-self-center">
            <div className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-body mb-3">
              Resources
            </div>
            <ul className="space-y-2 text-sm font-body">
              <li>
                <a
                  data-testid="footer-discord-link"
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onDiscordClick("footer-link")}
                  className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                >
                  <DiscordGlyph className="h-4 w-4" />
                  Discord
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-support-link"
                  href={`mailto:${GAME.supportEmail}`}
                  className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  {GAME.supportEmail}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-privacy-link"
                  href={GAME.privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                >
                  <Shield className="h-4 w-4" />
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>

          <div className="md:justify-self-end max-w-xs text-sm text-white/50 font-body">
            <div className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">
              Studio
            </div>
            VOLT GAMES, LDA — Lisboa, Portugal. Sniper Hunter is rated{" "}
            <span className="text-white/80">{GAME.ageRating}</span> · Violence,
            Blood.
          </div>
        </div>

        <p className="mt-12 text-[10px] uppercase tracking-[0.28em] text-white/30 font-body">
          Google Play and the Google Play logo are trademarks of Google LLC.
        </p>
      </div>
    </footer>
  );
}

/** Discord wordless glyph (Clyde) — pure SVG, no external deps. */
function DiscordGlyph({ className = "" }) {
  return (
    <svg
      viewBox="0 0 127.14 96.36"
      className={className}
      aria-hidden="true"
      preserveAspectRatio="xMidYMid meet"
    >
      <path
        fill="currentColor"
        d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"
      />
    </svg>
  );
}
